// src/components/AddBorrower.js
import {
  message,
  Card,
  Typography,
  Badge,
  Tag,
  AutoComplete,
  Upload,
  Tabs,
} from "antd";
import React, { useContext, useState } from "react";
import GenericForm from "../../components/GenericForm";
import BulkBorrowerSpreadsheet from "../../components/BulkBorrowerSpreadsheet";
import { AppContext } from "../../context/AppContext";
import JutemEtlUploadPage from "../jutemEtlUploadPage";
import axios from "axios";
import { uploadFile } from "../../utils/firebase";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import { InboxOutlined } from "@ant-design/icons";
import { getUserFriendlyError } from "../../utils/errorHandler";

const AddBorrower = () => {
  const { company } = useContext(AppContext);
  const token = localStorage.getItem("token");
  const [uploading, setUploading] = useState(false);
  const { Title, Text } = Typography;
  const [bulkUploading, setBulkUploading] = useState(false);
  const [borrowerType, setBorrowerType] = useState("individual");
  const navigate = useNavigate();

  // Function to generate random reference number
  const generateRefNumber = () => {
    const prefix = "REF";
    const timestamp = Date.now().toString().slice(-6); // Last 6 digits of timestamp
    const random = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, "0");
    return `${prefix}${timestamp}${random}`;
  };

  // Password validation standard
  const passwordRules = [
    { required: true, message: "Please input password!" },
    { min: 8, message: "Password must be minimum 8 characters" },
    { max: 20, message: "Password must be maximum 20 characters" },
    {
      pattern:
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
      message:
        "Must contain uppercase, lowercase, number, and special character",
    },
  ];

  // Email validation pattern (optional)
  const emailRules = [
    {
      type: "email",
      message: "Invalid email format",
    },
    {
      pattern: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
      message: "Invalid email format",
    },
  ];

  const employmentTypes = [
    { value: "fixed_term", label: "Fixed Term Contract" },
    { value: "permanent", label: "Permanent" },
    { value: "pensionable", label: "Pensionable" },
    { value: "business_person", label: "Business Owner" },
    { value: "freelancer", label: "Freelancer" },
    { value: "informal", label: "Informal" },
    { value: "formal_private", label: "Formal Private" },
    { value: "government", label: "Government Employee" },
  ];

  const industryTypes = [
    { value: "Manufacturing", label: "Manufacturing" },
    { value: "Healthcare", label: "Healthcare" },
    { value: "Financial Services", label: "Financial Services" },
    { value: "Retail", label: "Retail" },
    { value: "Agriculture", label: "Agriculture" },
    { value: "Energy", label: "Energy" },
    { value: "Construction", label: "Construction" },
    { value: "Transportation", label: "Transportation" },
    { value: "Information Technology", label: "Information Technology" },
    { value: "Real Estate", label: "Real Estate" },
    { value: "Telecommunications", label: "Telecommunications" },
    { value: "Business", label: "Business" },
    { value: "Education", label: "Education" },
    { value: "Hospitality", label: "Hospitality" },
    { value: "Tourism", label: "Tourism" },
    { value: "Mining", label: "Mining" },
    { value: "Other", label: "Other" },
  ];

  const provinceTypes = [
    { value: "Lusaka", label: "Lusaka" },
    { value: "Luapula", label: "Luapula" },
    { value: "Copperbelt", label: "Copperbelt" },
    { value: "Central", label: "Central" },
    { value: "North Western", label: "North Western" },
    { value: "Southern", label: "Southern" },
    { value: "Eastern", label: "Eastern" },
    { value: "Muchinga", label: "Muchinga" },
    { value: "Northern", label: "Northern" },
    { value: "Western", label: "Western" },
  ];

  const relationshipTypes = [
    { value: "spouse", label: "Spouse" },
    { value: "parent", label: "Parent" },
    { value: "child", label: "Child" },
    { value: "sibling", label: "Sibling" },
    { value: "friend", label: "Friend" },
    { value: "colleague", label: "Colleague" },
    { value: "other", label: "Other" },
  ];

  const formFields = [
    // Borrower Type Selection
    {
      label: "Borrower Type",
      name: "borrower_type",
      type: "select",
      colSpan: 24,
      options: [
        { value: "individual", label: "Individual" },
        { value: "sme", label: "SME (Small/Medium Enterprise)" },
      ],
      rules: [],
      initialValue: "individual",
      onChange: (value) => setBorrowerType(value),
    },

    // Personal Information Section (for Individual)
    {
      label: "First Name",
      name: "first_name",
      type: "input",
      colSpan: 12,
      rules: [],
      hidden: borrowerType === "sme",
    },
    {
      label: "Last Name",
      name: "last_name",
      type: "input",
      colSpan: 12,
      rules: [],
      hidden: borrowerType === "sme",
    },

    // SME Business Information
    {
      label: "Business Name",
      name: "business_name",
      type: "input",
      colSpan: 24,
      rules: [],
      hidden: borrowerType === "individual",
    },
    {
      label: "Business Registration Number",
      name: "business_registration_number",
      type: "input",
      colSpan: 12,
      rules: [],
      hidden: borrowerType === "individual",
    },
    {
      label: "TIN (Tax Identification Number)",
      name: "tin_number",
      type: "input",
      colSpan: 12,
      rules: [],
      hidden: borrowerType === "individual",
    },
    {
      label: "Email",
      name: "email",
      type: "input",
      colSpan: 12,
      rules: [
        ...emailRules,
        {
          validator: async (_, value) => {
            if (!value) return Promise.resolve();
            
            try {
              const response = await axios.get(
                `https://zedlenders.pickmesms.com/users?filter[email][_eq]=${value}&fields=id,email,first_name,last_name`,
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                  },
                }
              );
              
              if (response.data.data && response.data.data.length > 0) {
                const existingUser = response.data.data[0];
                return Promise.reject(
                  `This email already exists for ${existingUser.first_name} ${existingUser.last_name}`
                );
              }
              return Promise.resolve();
            } catch (error) {
              // If validation fails due to network issues, allow the form to proceed
              return Promise.resolve();
            }
          },
        },
      ],
    },
    {
      label: "Phone Number",
      name: "phone",
      type: "input",
      colSpan: 12,
      placeholder: "260...",
      rules: [
        {
          pattern: /^260[0-9]{9}$/,
          message: "Phone number must start with 260 followed by 9 digits (e.g., 260971234567)",
        },
      ],
    },
    {
      label: "NRC Number/ID/Passport",
      name: "nrc",
      type: "input",
      colSpan: 12,
      rules: [
        {
          validator: async (_, value) => {
            if (!value) return Promise.resolve();

            try {
              const response = await axios.get(
                `https://zedlenders.pickmesms.com/users?filter[nrc][_eq]=${value}&fields=id,nrc,first_name,last_name`,
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                  },
                }
              );

              if (response.data.data && response.data.data.length > 0) {
                const existingUser = response.data.data[0];
                return Promise.reject(
                  `This NRC/ID already exists for ${existingUser.first_name} ${existingUser.last_name}`
                );
              }
              return Promise.resolve();
            } catch (error) {
              // If validation fails due to network issues, allow the form to proceed
              // The server-side validation will catch duplicates
              return Promise.resolve();
            }
          },
        },
      ],
      hidden: borrowerType === "sme",
    },
    {
      label: "Province",
      name: "province",
      type: "select",
      colSpan: 12,
      options: provinceTypes,
      rules: [],
      hidden: borrowerType === "sme",
    },
    {
      label: (
        <span>
          Reference Number{" "}
          <span style={{ color: "#8c8c8c", fontWeight: "normal" }}>
            (Optional - System will generate if empty)
          </span>
        </span>
      ),
      name: "employee_number",
      type: "input",
      colSpan: 24,
      placeholder: "Leave empty to auto-generate",
      rules: [
        {
          pattern: /^[A-Za-z0-9-_]+$/,
          message: "Only letters, numbers, hyphens and underscores allowed",
        },
      ],
    },
    {
      label: borrowerType === "sme" ? "Business Physical Address" : "Address",
      name: "address",
      type: "textarea",
      colSpan: borrowerType === "sme" ? 12 : 24,
      rules: [],
    },
    // SME Business Operations

    // Employment Information Section (for Individuals only)
    {
      label: "Employment Type",
      name: "employment_type",
      type: "select",
      colSpan: 12,
      options: employmentTypes,
      rules: [],
      hidden: borrowerType === "sme",
    },

    // Industry (for both Individual and SME)
    {
      label: borrowerType === "sme" ? "Business Industry Sector" : "Industry",
      name: "industry",
      type: "select",
      colSpan: 12,
      options: industryTypes,
      rules: [],
    },

    // SME Ownership & Management - Directors Details
    {
      label: "Directors' Details (Name, ID Number, Phone)",
      name: "directors",
      type: "textarea",
      colSpan: 24,
      placeholder: "Enter each director's details on a new line:\nJohn Doe, 123456/78/1, +260971234567\nJane Smith, 987654/32/1, +260977654321",
      rules: [],
      hidden: borrowerType === "individual",
    },

    // Next of Kin Information Section (Optional)
    {
      label: "Next of Kin Name",
      name: "next_kin_name",
      type: "input",
      colSpan: 12,
      rules: [],
    },
    {
      label: "Next of Kin Phone",
      name: "next_kin_phone",
      type: "input",
      colSpan: 12,
      placeholder: "260...",
      rules: [
        {
          pattern: /^260[0-9]{9}$/,
          message: "Phone number must start with 260 followed by 9 digits (e.g., 260971234567)",
        },
      ],
    },
    {
      label: "Next of Kin Relation",
      name: "next_kin_relationship",
      type: "select",
      colSpan: 24,
      options: relationshipTypes,
      rules: [],
    },

    // Documents Section
    {
      label: "Registration Documents",
      name: "documents",
      type: "upload",
      colSpan: 24,
      props: {
        multiple: true,
        accept: "image/*,.pdf,.doc,.docx",
        maxCount: 5,
        listType: "picture-card",
      },
    },
  ];

  const handleSubmit = async (values) => {
    let payload = null;
    try {
      setUploading(true);

      // Check for duplicate NRC and Email (only for individuals)
      if (values.borrower_type === "individual") {
        try {
          const [nrcCheckResponse, emailCheckResponse] = await Promise.all([
            axios.get(
              `https://zedlenders.pickmesms.com/users?filter[nrc][_eq]=${values.nrc}&fields=id,nrc,first_name,last_name`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            ),
            axios.get(
              `https://zedlenders.pickmesms.com/users?filter[email][_eq]=${values.email}&fields=id,email,first_name,last_name`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            )
          ]);

          if (nrcCheckResponse.data.data && nrcCheckResponse.data.data.length > 0) {
            const existingUser = nrcCheckResponse.data.data[0];
            setUploading(false);
            message.error(
              `A borrower with NRC/ID "${values.nrc}" already exists: ${existingUser.first_name} ${existingUser.last_name}. Please check the existing records.`
            );
            return;
          }

          if (emailCheckResponse.data.data && emailCheckResponse.data.data.length > 0) {
            const existingUser = emailCheckResponse.data.data[0];
            setUploading(false);
            message.error(
              `A borrower with email "${values.email}" already exists: ${existingUser.first_name} ${existingUser.last_name}. Please check the existing records.`
            );
            return;
          }
        } catch (duplicateCheckError) {
          console.error("Error checking for duplicates:", duplicateCheckError);
          message.warning("Could not verify uniqueness. Proceeding with creation...");
        }
      } else {
        // For SMEs, check for duplicate business registration number
        try {
          const businessCheckResponse = await axios.get(
            `https://zedlenders.pickmesms.com/users?filter[business_registration_number][_eq]=${values.business_registration_number}&fields=id,business_name,business_registration_number`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (businessCheckResponse.data.data && businessCheckResponse.data.data.length > 0) {
            const existingBusiness = businessCheckResponse.data.data[0];
            setUploading(false);
            message.error(
              `A business with registration number "${values.business_registration_number}" already exists: ${existingBusiness.business_name}. Please check the existing records.`
            );
            return;
          }
        } catch (duplicateCheckError) {
          console.error("Error checking for duplicates:", duplicateCheckError);
          message.warning("Could not verify uniqueness. Proceeding with creation...");
        }
      }

      // Helper function to upload files
      const uploadFiles = async (fileField) => {
        if (fileField?.fileList && fileField.fileList.length > 0) {
          return await Promise.all(
            fileField.fileList.map(async (file) => {
              const url = await uploadFile(file.originFileObj, 32982874);
              return url;
            })
          );
        }
        return [];
      };

      // Handle all document uploads
      const documents = await uploadFiles(values.documents);

      // Build base payload
      payload = {
        borrower_type: values.borrower_type,
        email: values.email,
        phone: values.phone,
        address: values.address,
        industry: values.industry,
        employee_number: values.employee_number || generateRefNumber(),
        company: company.id,
        role: "ab3c07d3-a225-4b68-8454-96e00116e307",
        documents: documents,
        status: "active",
        password: "123456",
      };

      // Add individual-specific fields
      if (values.borrower_type === "individual") {
        payload.first_name = values.first_name;
        payload.last_name = values.last_name;
        payload.nrc = values.nrc;
        payload.province = values.province;
        payload.employment_type = values.employment_type;
      } else {
        // Add SME-specific fields
        payload.business_name = values.business_name;
        payload.business_registration_number = values.business_registration_number;
        payload.tin_number = values.tin_number;
        payload.directors = values.directors; // Store as text for now

        // For SME, use business name as first_name and set last_name to "SME"
        payload.first_name = values.business_name;
        payload.last_name = "SME";

        // SME businesses don't have NRC - use registration number as unique identifier
        payload.nrc = values.business_registration_number;
      }

      // Only add next of kin fields if they exist
      if (values.next_kin_name) payload.next_kin_name = values.next_kin_name;
      if (values.next_kin_phone) payload.next_kin_phone = values.next_kin_phone;
      if (values.next_kin_relationship)
        payload.next_kin_relationship = values.next_kin_relationship;

      console.log("Sending payload:", payload);

      const response = await axios.post(
        "https://zedlenders.pickmesms.com/users",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setUploading(false);

      if (response.data.data) {
        message.success("Borrower created successfully!");
        navigate(`/borrower/${response.data.data.id}`);
      }
    } catch (error) {
      console.error("API Error:", error.response?.data);
      console.error("Payload sent:", payload);
      setUploading(false);
      message.error(getUserFriendlyError(error));
    }
  };

  const handleBulkUpload = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        setBulkUploading(true);
        const csvData = e.target.result;
        const results = Papa.parse(csvData, {
          header: true,
          skipEmptyLines: true,
        });

        const successes = [];
        const errors = [];

        for (const row of results.data) {
          try {
            // Check for duplicate NRC first
            try {
              const duplicateCheckResponse = await axios.get(
                `https://zedlenders.pickmesms.com/users?filter[nrc][_eq]=${row.nrc}&fields=id,nrc,first_name,last_name`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );
              
              if (duplicateCheckResponse.data.data && duplicateCheckResponse.data.data.length > 0) {
                const existingUser = duplicateCheckResponse.data.data[0];
                errors.push({
                  row,
                  error: `Duplicate NRC/ID "${row.nrc}" - already exists for ${existingUser.first_name} ${existingUser.last_name}`,
                });
                continue; // Skip this row
              }
            } catch (duplicateCheckError) {
              console.error("Error checking for duplicate NRC in bulk upload:", duplicateCheckError);
              // Continue with creation if the duplicate check fails
            }

            const payload = {
              first_name: row.first_name,
              last_name: row.last_name,
              employee_number: row.reference_number || generateRefNumber(), // Use reference_number from CSV
              phone: row.phone.toString(), // Ensure string format
              nrc: row.nrc,
              address: row.address,
              province: row.province,
              employment_type: row.employment_type,
              industry: "Business", // Default industry as per requirements
              business: row.business, // Map business field
              department: row.department,
              email: row.email,
              next_kin_name: row.next_kin_name,
              next_kin_phone: row.next_kin_phone,
              next_kin_relationship: row.next_kin_relationship,
              company: company.id, // Company ID from localStorage
              role: "ab3c07d3-a225-4b68-8454-96e00116e307",
              status: "active",
              password: "123456", // Default password
              email_notifications: true, // Default value
              provider: "default", // Default value
              credit_score: 650, // Default credit score
              documents: [],
            };

            await axios.post(
              "https://zedlenders.pickmesms.com/users",
              payload,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
            successes.push(row);
          } catch (err) {
            errors.push({
              row,
              error:
                err.response?.data?.errors?.[0]?.message || "Unknown error",
            });
          }
        }

        message.success(
          `Bulk upload complete: ${successes.length} successful, ${errors.length} failed`
        );
        if (errors.length > 0) {
          console.error("Failed entries:", errors);
        }
        navigate("/borrowers");
      } catch (error) {
        message.error("Error processing CSV file");
      } finally {
        setBulkUploading(false);
      }
    };
    reader.readAsText(file);
    return false; // Prevent default upload
  };

  const tabItems = [
    {
      key: '1',
      label: 'Individual Form',
      children: (
        <Card>
          <Title level={3} style={{ textAlign: "center", marginBottom: 24 }}>
            New Client
          </Title>
          <GenericForm
            fields={formFields}
            onFinish={handleSubmit}
            submitButtonText="Create"
            submitButtonProps={{
              loading: uploading,
              disabled: uploading,
              loadingText: "Processing...",
              style: { marginTop: "20px" },
            }}
            layout="vertical"
          />
        </Card>
      ),
    },
    {
      key: '2',
      label: 'Live Spreadsheet',
      children: <BulkBorrowerSpreadsheet />,
    },
    {
      key: '3',
      label: 'CSV Upload',
      children: (
        <Card>
          <Title level={3} style={{ textAlign: "center", marginBottom: 24 }}>
            Bulk Upload Clients
          </Title>
          <Upload.Dragger
            accept=".csv"
            beforeUpload={handleBulkUpload}
            showUploadList={false}
            disabled={bulkUploading}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag CSV file to this area
            </p>
            <p className="ant-upload-hint">
              CSV format only. Download{" "}
              <a
                href="/sample_borrowers_upload.csv"
                download="sample_borrowers_upload.csv"
                onClick={(e) => e.stopPropagation()}
              >
                sample template
              </a>
              .
            </p>
          </Upload.Dragger>
          {bulkUploading && (
            <div style={{ marginTop: 16, textAlign: "center" }}>
              <Text type="secondary">
                Processing upload... Please don't close the page.
              </Text>
            </div>
          )}
        </Card>
      ),
    },
  ];

  if (company?.id === 22) {
    return <JutemEtlUploadPage />;
  }

  return (
    <div style={{ padding: '0 16px' }}>
      <Tabs items={tabItems} size="large" />
    </div>
  );
};

export default AddBorrower;
